import React from 'react';
import { FormattedMessage } from 'react-intl';
import StatusBox from './StatusBox';
import ExpansionPanel, { isMaterialVisible } from './ExpansionPanel';

interface ProductRowProps {
    product: any;
    index: number;
    isAdmin: boolean;
    userResponsibilityIds: number[];
    flowMode?: 'linear' | 'free';
    colWidths: { client: number; project: number; product: number; qty: number };
    selectedBox: { productId: number; responsibilityId: number } | null;
    onBoxClick: (responsibilityId: number) => void;
    onClose: () => void;
    onStatusUpdated: () => void;
    onRowClick?: () => void;
    readOnly?: boolean;
    isResponsibilityVisible?: (responsibilityId: number) => boolean;
    // Deep-link from a notification (set by the board's URL handler):
    //   - isFlashing → applies the pulse highlight class for ~2.5s
    //   - initialMaterialId → if the row is also auto-opened, pre-select
    //     this material inside the ExpansionPanel so the user lands on the
    //     specific material whose comment they were notified about
    isFlashing?: boolean;
    initialMaterialId?: number | null;
}

const ProductRow: React.FC<ProductRowProps> = ({ product, index, isAdmin, userResponsibilityIds, flowMode = 'linear', colWidths, selectedBox, onBoxClick, onClose, onStatusUpdated, onRowClick, readOnly, isResponsibilityVisible, isFlashing, initialMaterialId }) => {
    const isExpanded = selectedBox?.productId === product.productId;
    const selectedStatus = isExpanded
        ? product.statuses.find((s: any) => s.responsibilityId === selectedBox?.responsibilityId)
        : null;

    // Simple users only see their assigned, non-completed responsibilities. In 'linear'
    // flow mode the prerequisite chain also applies (status hidden if upstream isn't ready);
    // in 'free' mode the chain is skipped so workers see every step in their responsibility
    // regardless of upstream state. Admin and readOnly (archive) views show all statuses.
    const roleStatuses = (isAdmin || readOnly)
        ? product.statuses
        : product.statuses.filter((s: any) => {
            if (!userResponsibilityIds.includes(s.responsibilityId)) return false;
            if (s.responsibilityStatusId === 1) return false;
            if (flowMode === 'linear') {
                const statusIndex = product.statuses.findIndex((st: any) => st.responsibilityId === s.responsibilityId);
                if (s.hasMaterials) {
                    // Has materials — at least one must be visible (prerequisite chain met)
                    const hasWorkableMaterial = (s.materialStatuses || []).some((m: any) =>
                        isMaterialVisible(m.materialId, m.temporaryMaterialId, s.responsibilityId, product.statuses, false, flowMode)
                    );
                    if (!hasWorkableMaterial) return false;
                } else {
                    // No materials — previous process must not be waiting (status 2)
                    if (statusIndex > 0 && product.statuses[statusIndex - 1].responsibilityStatusId === 2) return false;
                }
            }
            return true;
        });

    // Category filter (board "Procese të prodhimit" / "Aktivitet (Projektimi)"):
    // hide the status boxes outside the active category. A row left with no
    // boxes is dropped below — so selecting a category also filters the rows.
    const visibleStatuses = roleStatuses.filter((s: any) =>
        isResponsibilityVisible ? isResponsibilityVisible(s.responsibilityId) : true
    );

    // Don't render the row at all if there's nothing to work on
    if (visibleStatuses.length === 0) return null;

    return (
        <div
            className={`npb-row-wrapper ${isExpanded ? 'expanded' : ''} ${isFlashing ? 'is-flashing' : ''}`}
            data-product-id={product.productId}
        >
            <div className="npb-row">
                <div className="npb-row-sticky" onClick={onRowClick} style={{ cursor: 'pointer' }}>
                    <span className="npb-cell npb-col-num">{index + 1}</span>
                    <span className="npb-cell" style={{ width: colWidths.client, minWidth: 50 }} title={product.clientName}>
                        {product.clientName || '\u2014'}
                    </span>
                    <span className="npb-cell" style={{ width: colWidths.project, minWidth: 50 }} title={product.projectName}>
                        {product.projectName || '\u2014'}
                    </span>
                    <span className="npb-cell" style={{ width: colWidths.product, minWidth: 50 }} title={`${product.productName} ${product.productCode ? `(${product.productCode})` : ''}`}>
                        {product.productName || '\u2014'}
                        {product.productCode && <span className="npb-product-code">{product.productCode}</span>}
                    </span>
                    <span className="npb-cell" style={{ width: colWidths.qty, minWidth: 50 }}>
                        {product.quantity}{product.unit ? <span className="npb-unit">{product.unit}</span> : ''}
                    </span>
                </div>
                <div className="npb-statuses">
                    {product.measureStatus !== 2 ? (
                        <div className="npb-measure-warning">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            <span><FormattedMessage id="production.row.measureWarning" defaultMessage="Matja fillestare — nuk mund të punoni në këtë produkt ende" /></span>
                        </div>
                    ) : (
                        visibleStatuses.map((status: any) => (
                            <StatusBox
                                key={status.responsibilityId}
                                status={status}
                                isAdmin={isAdmin}
                                isSelected={isExpanded && selectedBox?.responsibilityId === status.responsibilityId}
                                onClick={() => onBoxClick(status.responsibilityId)}
                            />
                        ))
                    )}
                </div>
            </div>

            {isExpanded && selectedStatus && (
                <ExpansionPanel
                    product={product}
                    status={selectedStatus}
                    isAdmin={isAdmin}
                    userResponsibilityIds={userResponsibilityIds}
                    flowMode={flowMode}
                    onClose={onClose}
                    onStatusUpdated={onStatusUpdated}
                    readOnly={readOnly}
                    initialMaterialId={initialMaterialId}
                />
            )}
        </div>
    );
};

export default ProductRow;
